<template>
  <div class="airbus-content main-content wide">
    <div class="wrap">
      <div class="video-wrapper">
        <video
          v-if="!isMobile && $_WebHelpers_supportshH264BaselineVideo()"
          autoplay
          muted
          loop
          class="top-video"
        >
          <source
            :src="pageData.desktopTopVideo"
            type="video/mp4"
          >
        </video>

        <img
          v-else
          :src="pageData.mobileTopImg"
          class="image-video"
        >
      </div>

      <div class="title-block">
        <h2 class="page-subtitle">{{ pageData.subtitle }}</h2>
        <h1>{{ pageData.pageTitle }}</h1>
        <div
          v-if="pageData.topLinkUrl !== ''"
          class="btn btn-blue"
        >
          <a :href="pageData.topLinkUrl">{{ pageData.topLinkText }}</a>
        </div>
      </div>

      <div class="features-block">
        <div
          v-for="(item, index) in pageData.features"
          :key="index"
          class="feature-item"
        >
          <div
            class="feature-image"
            :style="responsiveImage(item)"
          />
          <div class="feature-content">
            <div class="feature-inner">
              <div class="feature-title">{{ item.title }}</div>
              <div class="feature-description">{{ item.description }}</div>
              <a
                v-if="item.url !== ''"
                class="feature-link"
                :href="item.url"
                :title="item.title"
                target="_blank"
              >
                {{ $t('link.readMore') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="explore-block"
      :style="$_ImageHelpers_getBackgroundImage(pageData.explore.img)"
    >
      <div class="wrap">
        <div class="explore-content">
          <div class="explore-title">{{ pageData.explore.title }}</div>
          <div
            v-if="pageData.explore.url !== ''"
            class="btn btn-blue"
          >
            <a
              target="_blank"
              :href="pageData.explore.url"
            >
              {{ pageData.explore.buttonText }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="video-block">
      <div class="video-title">{{ pageData.videoTitle }}</div>
      <carousel
        :navigation-enabled="true"
        :navigation-next-label="''"
        :navigation-prev-label="''"
        :center-mode="true"
        :loop="true"
        :per-page-custom="[[320, 1], [768, 4]]"
      >
        <slide
          v-for="(videoItem, index) in pageData.videos"
          :id="`video-${videoItem.id}`"
          :key="index"
        >
          <div
            class="video-item"
            :style="$_ImageHelpers_getBackgroundImage(videoItem.img)"
            @click="clickVideo(videoItem.id)"
          >
            <div class="video-description">{{ videoItem.title }}</div>
          </div>
        </slide>
      </carousel>
    </div>

    <div class="blog-block">
      <div class="wrap">
        <div class="blog-header">
          <div class="blog-subtitle">{{ pageData.blog.subtitle }}</div>
          <div class="blog-title">{{ pageData.blog.title }}</div>
        </div>
        <div class="blog-list">
          <div
            v-for="(item, index) in pageData.blog.articles"
            :key="index"
            class="blog-item"
          >
            <div
              class="blog-image"
              :style="$_ImageHelpers_getBackgroundImage(item.img)"
            />
            <div class="blog-item-content">
              <div class="blog-description">{{ item.description }}</div>
              <a
                v-if="item.url !== ''"
                class="read-more"
                :href="item.url"
              >
                {{ $t('link.readMore') }}
              </a>
            </div>
          </div>
        </div>
        <div
          v-if="pageData.blog.linkUrl !== ''"
          class="btn btn-ghost"
        >
          <a :href="pageData.blog.linkUrl">{{ pageData.blog.linkTitle }}</a>
        </div>
      </div>
    </div>

    <div
      v-if="isVideoPopupOpen"
      class="player-popup"
    >
      <div class="background-overlay-light" />
      <div class="video-wrap">
        <div
          id="video-player"
          style="width: 100%; height: 100%;"
        />
        <close-icon @click="closeVideoPopup()" />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import { Carousel, Slide } from 'vue-carousel'
  import CloseIcon from 'components/CloseIcon'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import WebHelpers from 'utils/mixins/WebHelpers'

  export default {
    components: {
      Carousel,
      Slide,
      CloseIcon
    },
    mixins: [
      ImageHelpers,
      WebHelpers
    ],
    data () {
      return {
        isVideoPopupOpen: false
      }
    },
    computed: {
      ...mapGetters('device', [
        'isMobile'
      ]),

      ...mapState('airbus', [
        'pageData'
      ])
    },
    mounted () {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      const firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
    },
    methods: {
      responsiveImage(data) {
        const imgData = {
          desktop: data.img.source,
          tablet: data.tabletImg.source,
          mobile: data.mobileImg.source
        }

        return this.$_ImageHelpers_getBackgroundImage(
          this.$_ImageHelpers_responsiveImage(imgData)
        )
      },

      clickVideo (videoId) {
        this.isVideoPopupOpen = true
        setTimeout(() => {
          this.onYouTubeIframeAPIReady(videoId)
        }, 100)
      },

      onYouTubeIframeAPIReady (id) {
        // eslint-disable-next-line no-undef, no-new
        new YT.Player('video-player', {
          videoId: id,
          playerVars: {
            autoplay: 1
          },
          events: {
            onReady: this.onPlayerReady
          }
        })
      },

      onPlayerReady (event) {
        event.target.playVideo()
      },

      closeVideoPopup () {
        this.isVideoPopupOpen = false
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";
  @import "~assets/styles/components/carousel-navigation-common.scss";

  .airbus-content {
    background-color: $grey-white-2;

    @include bp-mf-tablet {
      background-color: $formBlock;
    }
  }

  .video-wrapper {

    .image-video {
      max-width: 100%;
    }
  }

  .top-video {
    width: 100%;
  }

  h1 {
    @include h1Style();
    text-align: center;
  }

  .btn {
    height: 48px;

    @include bp-mf-tablet {
      height: 52px;
    }

    a {
      padding: 0 16px;
      line-height: 50px;

      @include bp-mf-tablet {
        line-height: 54px;
      }
    }
  }

  .title-block {
    padding: 30px 23px;
    text-align: center;

    @include bp-mf-tablet {
      padding: 60px 26px;
    }

    .btn {
      min-width: 150px;
    }
  }

  .page-subtitle {
    @include body14Style();
    text-align: center;
  }

  .features-block {
    display: flex;
    flex-direction: column;
  }

  .feature-item {
    display: flex;
    background-color: #ffffff;
    flex-direction: column;

    @include bp-mf-tablet {
      flex-direction: row;
      min-height: 560px;
    }

    @include bp-mf-desktop {
      height: 560px;
    }

    &:nth-child(2n) {

      .feature-content {

        @include bp-mf-tablet {
          order: 1;
        }
      }

      .feature-image {

        @include bp-mf-tablet {
          order: 2;
        }
      }
    }
  }

  .feature-content {
    display: flex;
    align-items: center;
    padding: 27px 23px 40px;

    @include bp-mf-tablet {
      padding: 45px;
      width: 50%;
    }

    @include bp-mf-desktop {
      padding: 15px 85px;
    }
  }

  .feature-image {
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;

    @include bp-mf-tablet {
      flex: 1;
      width: 50%;
    }

    @include bp-mobile {
      padding-bottom: 79%;
    }
  }

  .feature-title {
    @include h3Style();
  }

  .feature-description {
    @include body16Style();
  }

  .feature-link,
  .read-more {
    font-weight: 500;
    text-decoration: none;
    color: $blue-medium;
    line-height: 17px;
    margin-top: 3px;
    display: inline-flex;
    align-items: center;

    @include bp-mf-tablet {
      font-size: em(16);
      line-height: 18px;
    }

    &:after {
      content: $icon-arrow-right-thin-medium;
      @include icon-font-thin();
      vertical-align: top;
      font-size: 12px;
      line-height: 18px;
      margin-left: 3px;

      @include bp-mf-tablet {
        line-height: 20px;
      }
    }

    &:hover {

      @include bp-mf-tablet {
        color: $blue-light;
      }
    }
  }

  .explore-block {
    display: flex;
    align-items: center;
    padding: 0 23px;
    height: 150px;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    text-align: center;

    @include bp-mf-tablet {
      padding: 0 26px;
      height: 300px;
      text-align: left;
    }

    .wrap {

      @include bp-mf-tablet {
        width: 100%;
      }

      @include bp-desktop-small {
        padding: 0;
      }
    }
  }

  .explore-content {
    margin: 0 auto;

    @include bp-tablet-only {
      max-width: 95%;
    }

    @include bp-mf-desktop {
      max-width: 85%;
    }

    @include bp-desktop-small {
      max-width: 88%;
    }

    .btn {
      min-width: auto;

      a {
        text-transform: uppercase;
      }
    }
  }

  .explore-title {
    @include h3Style();
    margin: 0 auto 10px;
    color: #ffffff;

    @include bp-mf-tablet {
      margin: 0 0 16px;
      max-width: 500px;
      text-transform: uppercase;
    }
  }

  .video-block {
    margin: 0 auto;
    padding: 30px 23px;
    width: 100%;

    @include bp-mf-tablet {
      margin-bottom: 0;
      padding: 60px 0 0;
    }

    /deep/ {

      .VueCarousel-pagination {
        margin: 15px 0 0;
      }

      .VueCarousel-navigation-button {

        @include bp-mf-tablet {
          display: none;
        }
      }
    }
  }

  .video-title {
    @include h3Style();
    position: relative;
    margin: 0 auto 30px;
    text-align: center;
    text-transform: uppercase;

    @include bp-mf-tablet {
      margin-bottom: 40px;
    }

    &:after {
      content: '';
      position: absolute;
      right: 0;
      left: 0;
      bottom: -10px;
      margin: 0 auto;
      width: 44px;
      height: 2px;
      background: $green-base;

      @include bp-mf-tablet {
        bottom: -15px;
        width: 40px;
        height: 3px;
      }
    }
  }

  .VueCarousel-slide {
    position: relative;
    padding-bottom: 50%;

    @include bp-mf-tablet {
      padding-bottom: 25%;
    }

    &:hover {

      &:before,
      &:after {

        @include bp-mf-tablet {
          display: block;
        }
      }
    }

    &:before,
    &:after {
      content: '';
      z-index: 1;
      position: absolute;
      bottom: 50%;
      transform: translateX(-50%) translateY(50%);
      pointer-events: none;

      @include bp-mf-tablet {
        display: none;
        transform: none;
      }
    }

    &:before {
      left: 50%;
      width: 50px;
      height: 50px;
      border: 2px solid #ffffff;
      border-radius: 27px;
      line-height: 50px;
      font-size: em(20);
      font-weight: 700;
      text-align: center;
      color: #ffffff;

      @include bp-mf-tablet {
        left: 30px;
        bottom: 30px;
      }

      @include bp-tablet-only {
        bottom: 20px;
        left: 20px;
        width: 40px;
        height: 40px;
      }
    }

    &:after {
      left: 50.4%;
      width: 0;
      height: 0;
      border-top: 7px solid transparent;
      border-bottom: 7px solid transparent;
      border-left: 9px solid #ffffff;

      @include bp-mf-tablet {
        left: 53px;
        bottom: 50px;
        border-left: 12px solid #ffffff;
      }

      @include bp-tablet-only {
        bottom: 35px;
        left: 38px;
      }
    }
  }

  .video-item {
    position: absolute;
    width: 100%;
    height: 100%;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;

    @include bp-mf-tablet {
      background-size: contain;
      cursor: pointer;
    }

    &:after {

      @include bp-mf-tablet {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: $blue-medium;
        opacity: 0;
        transition: all cubic-bezier(0.4, 0.52, 0.15, 0.99) 0.3s;
      }
    }

    &:hover {

      &:after {

        @include bp-mf-tablet {
          opacity: 0.7;
        }
      }

      .video-description {

        @include bp-mf-tablet {
          display: block;
        }
      }
    }
  }

  .video-description {
    @include body16Style();
    z-index: 1;
    display: none;
    position: absolute;
    top: 52px;
    left: 30px;
    width: 180px;
    color: #ffffff;

    @include bp-tablet-only {
      top: 35px;
      left: 20px;
      width: 170px;
    }
  }

  .blog-block {
    position: relative;
    padding: 30px 23px;
    background-color: #ffffff;

    @include bp-mf-tablet {
      padding: 60px 26px;
      background-color: $formBlock;
    }

    > .wrap {
      @include clearfix();

      @include bp-mf-tablet {
        position: relative;
        padding-top: 60px;
        border-top: 1px solid $formInputBorder;
      }
    }

    .btn {

      @include bp-mf-tablet {
        position: absolute;
        bottom: 0;
        left: 0;
      }
    }
  }

  .blog-header {
    text-align: center;

    @include bp-tablet-only {
      width: 230px;
    }

    @include bp-mf-tablet {
      display: inline-block;
      padding-right: 20px;
      text-align: left;
    }

    @include bp-mf-desktop {
      width: 40%;
    }
  }

  .blog-subtitle {
    @include h4Style();
  }

  .blog-title {
    @include h3Style();
    text-transform: uppercase;
  }

  .blog-list {
    margin-bottom: 5px;

    @include bp-tablet-only {
      width: calc(100% - 230px);
    }

    @include bp-mf-tablet {
      display: inline-block;
      vertical-align: top;
      float: right;
      margin: 0;
      text-align: right;
    }

    @include bp-mf-desktop {
      width: 60%;
    }
  }

  .blog-item {
    padding: 20px 0;
    border-bottom: 1px solid $formInputBorder;

    @include bp-mf-tablet {
      display: inline-block;
      vertical-align: top;
      margin-right: 24px;
      padding: 0;
      width: calc(50% - 14px);
      max-width: 280px;
      border-bottom: none;
      text-align: left;
    }

    &:last-child {
      margin-right: 0;
      border-bottom: none;
    }
  }

  .blog-image {
    position: relative;
    display: inline-block;
    vertical-align: top;
    padding-bottom: 36%;
    width: calc(50% - 6px);
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;

    @include bp-mf-tablet {
      display: block;
      margin-bottom: 26px;
      padding-bottom: 0;
      width: 100%;
      height: 165px;
    }
  }

  .blog-item-content {
    display: inline-block;
    vertical-align: top;
    padding: 0 0 0 12px;
    width: 50%;

    @include bp-mf-tablet {
      display: block;
      padding: 0;
      width: 100%;
    }

    .read-more {
      font-size: em(12);

      @include bp-mf-tablet {
        font-size: em(16);
      }

      &:after {
        line-height: 18px;
      }
    }
  }

  .blog-description {
    @include body14Style();
  }

  .player-popup {
    position: fixed;
    z-index: 100;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
  }

  .video-wrap {
    position: absolute;
    z-index: 101;
    padding: 60px 23px 23px;
    width: 100%;
    height: 100%;
    background-color: #ffffff;

    @include bp-mf-tablet {
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
      padding: 60px 33px 33px;
      width: 70%;
      height: 70%;
      border-radius: 10px;
    }
  }

  .close-icon {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: em(16);
    color: $grey;
  }
</style>
